import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
    FormGroup,
    FormHelperText,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
} from '@mui/material';
import { RefObject, useState, type JSX } from 'react';
import { useLocale } from '@repo/i18n';
import TextFieldStyleUtils from 'src/components/utils/jss/TextFieldStyleUtils';

type NativePasswordInputProps = {
    value: string;
    onChange: (value: string) => void;
    label: string;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    autoComplete?: HTMLInputElement['autocomplete'];
    inputRef?: RefObject<HTMLInputElement | undefined | null>;
};

// Password field mirroring TextInput's styling, with a show/hide toggle. Kept as a
// dedicated native component so the shared TextInput (type="text" only) is untouched.
export default function NativePasswordInput(props: NativePasswordInputProps): JSX.Element {
    const { value, onChange, label, required, error, helperText, autoComplete, inputRef } = props;
    const { t } = useLocale();
    const [visible, setVisible] = useState(false);

    return (
        <FormGroup>
            <InputLabel disableAnimation required={required}>
                {label}
            </InputLabel>
            <OutlinedInput
                required={required}
                inputRef={inputRef}
                value={value}
                onChange={(e) => {
                    if (e.target instanceof HTMLInputElement) onChange(e.target.value);
                }}
                type={visible ? 'text' : 'password'}
                size="small"
                name="password"
                // While revealed the input is type="text" — without this the browser would
                // spellcheck (and potentially remote-process) the visible password.
                spellCheck={false}
                autoComplete={autoComplete}
                error={error}
                inputProps={{ sx: TextFieldStyleUtils.timeslotsInputValueColorSx, maxLength: 100 }}
                sx={{ width: '100%' }}
                endAdornment={
                    <InputAdornment position="end">
                        <IconButton
                            aria-label={visible ? t.hide_password : t.show_password}
                            onClick={() => setVisible((v) => !v)}
                            edge="end"
                            tabIndex={-1}
                        >
                            {visible ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </InputAdornment>
                }
            />
            {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
        </FormGroup>
    );
}
